import * as React from 'react';
import styled from 'styled-components';

import { Box } from '@origin-digital/style-guide/dist/cjs/components/Box';

import { BREAK_POINTS, COLOURS, TEXT } from '../../consts/style';
import { IBannerImage } from '../../models/content.interfaces';
import { DangerousHtml } from '../DangerousHtml';
import { CallToActions } from '../CallToAction/CallToAction';
import { getCloudinaryUrl } from '../../services/cloudinary';
import { GoodEnergy } from '../GoodEnergy';
import { IVerticalOffset } from '../Container';
import { DiscountItems } from '../Discount/Discount';
import { Heading3 } from '../Text/Text';
import { BannerPostcodePrompt } from '../BannerPostcodePrompt';

interface BannerProps {
  backgroundColour: string;
  backgroundImage: string;
}

const BannerContainer = styled.div`
  display: flex;
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  overflow: auto;
  z-index: 0;
  border: 0;

  min-height: 436px;

  background-image: ${(p: BannerProps) =>
    p.backgroundImage ? 'url(' + p.backgroundImage + ')' : ''};

  background-color: white;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 50% 50%;

  @media screen and (min-width: ${BREAK_POINTS.MIN.SM}) {
    min-height: 384px;
  }

  @media screen and (min-width: ${BREAK_POINTS.MIN.MD}) {
    min-height: 446px;
  }
` as any;

const ContentContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  min-height: 320px;
  width: 734px;

  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    width: 960px;
  }

  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    width: 1140px;
  }
`;

const ContentSpacing = styled.div`
  margin-left: auto;
  margin-right: auto;

  background-color: white;
  text-align: left;
  padding: 24px;
  width: 366px;

  margin: 24px 0;
  margin-bottom: ${({ verticalOffset }: IVerticalOffset) => (verticalOffset || 0) + 32}px;

  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    padding: 32px;
    width: 520px;
  }

  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    padding: 32px;
    width: 555px;
  }
`;

const Title = styled.h1`
  font-weight: ${({ withLogo }: { withLogo: boolean }) => (withLogo ? '600' : '700')};
  font-size: ${({ withLogo }: { withLogo: boolean }) => (withLogo ? '20px' : '24px')};
  line-height: 24px;
  margin: 0 0 16px 0;
  color: ${TEXT.HEADING};

  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    font-size: ${({ withLogo }: { withLogo: boolean }) => (withLogo ? '20px' : '32px')};
    line-height: 28px;
  }
` as any;

const DiscountHeading = styled(Heading3.withComponent('h1'))`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
`;

const ByLine = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  margin: 0 0 16px 0;
  color: ${COLOURS.PRIMARY.GREY};

  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

const PricingInfo = styled.div`
  color: ${COLOURS.PRIMARY.GREY};
  font-size: 14px;
  a {
    color: ${COLOURS.PRIMARY.GREY};
    text-decoration: underline;

    &:focus,
    &:hover {
      color: ${COLOURS.PRIMARY.RED};
      text-decoration: none;
    }
  }
`;

const PlanDisclaimer = styled(PricingInfo)`
  font-size: 12px;
`;

const FlexRow = styled.div`
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    display: flex;
    flex-direction: row;
  }
`;

export const BannerImage = ({
  bylineHtml = '',
  headline = '',
  imageContentBoxAlign,
  image,
  imageAltText,
  bannerStyle = 'standard',
  ctas,
  discounts,
  verticalOffset, // only used if the below element is overlapping
  showDisclaimer,
  pricingText = '',
  disclaimerText = '',
  postcodeEntry = false,
}: IBannerImage & IVerticalOffset) => {
  const goodEnergyImg = bannerStyle === 'good-energy';
  return (
    <BannerContainer
      data-comment="BannerImage"
      backgroundImage={getCloudinaryUrl(image)}
      title={imageAltText}
    >
      <ContentContainer>
        {postcodeEntry ? (
          <BannerPostcodePrompt />
        ) : (
          <ContentSpacing verticalOffset={verticalOffset}>
            {goodEnergyImg && <GoodEnergy />}
            {bannerStyle === 'discount' ? (
              <DiscountHeading>{headline}</DiscountHeading>
            ) : (
              <Title withLogo={goodEnergyImg} alignContent={imageContentBoxAlign}>
                {headline}
              </Title>
            )}

            {bannerStyle === 'discount' ? (
              <DiscountItems showDisclaimer={showDisclaimer} discounts={discounts} />
            ) : (
              <ByLine>
                <DangerousHtml html={bylineHtml} />
              </ByLine>
            )}
            <FlexRow>
              <CallToActions ctas={ctas} trackingCode="bannerButton" />
              {pricingText ? (
                <Box marginTop={4}>
                  <PricingInfo dangerouslySetInnerHTML={{ __html: pricingText }} />
                </Box>
              ) : null}
            </FlexRow>
            {disclaimerText ? (
              <Box marginTop={6}>
                <PlanDisclaimer dangerouslySetInnerHTML={{ __html: disclaimerText }} />
              </Box>
            ) : null}
          </ContentSpacing>
        )}
      </ContentContainer>
    </BannerContainer>
  );
};
